import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

type Status = 'loading' | 'success' | 'already' | 'error' | 'needLogin';

export default function CreditJoinPage() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token');
  const merchantParam = params.get('merchant');
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');

  const run = useCallback(async () => {
    if (!user) {
      setStatus('needLogin');
      return;
    }
    setStatus('loading');
    try {
      if (token) {
        const { data: inv } = await supabase
          .from('credit_invitations')
          .select('*')
          .eq('token', token)
          .maybeSingle();
        if (!inv) {
          setStatus('error');
          setMessage('Invitation introuvable ou expirée.');
          return;
        }
        if (inv.status === 'accepted' && inv.invited_user_id !== user.id) {
          setStatus('error');
          setMessage('Cette invitation a déjà été utilisée.');
          return;
        }
        await supabase
          .from('credit_customers')
          .update({ linked_user_id: user.id, updated_at: new Date().toISOString() })
          .eq('id', inv.customer_id);
        await supabase
          .from('credit_invitations')
          .update({ status: 'accepted', invited_user_id: user.id, accepted_at: new Date().toISOString() })
          .eq('id', inv.id);
        setStatus('success');
        setMessage('Votre compte est maintenant lié à ce commerçant.');
        return;
      }

      if (merchantParam) {
        const merchantId = parseInt(merchantParam, 10);
        if (Number.isNaN(merchantId)) {
          setStatus('error');
          setMessage('Commerçant invalide.');
          return;
        }
        const { data: existing } = await supabase
          .from('credit_customers')
          .select('id')
          .eq('merchant_id', merchantId)
          .eq('linked_user_id', user.id)
          .maybeSingle();
        if (existing) {
          setStatus('already');
          setMessage('Votre compte est déjà lié à ce commerçant.');
          return;
        }
        // Rattachement automatique par téléphone si le commerçant a déjà créé la fiche
        let linked = false;
        if (user.telephone) {
          const { data: byPhone } = await supabase
            .from('credit_customers')
            .select('id')
            .eq('merchant_id', merchantId)
            .eq('phone', user.telephone)
            .is('linked_user_id', null)
            .maybeSingle();
          if (byPhone) {
            await supabase
              .from('credit_customers')
              .update({ linked_user_id: user.id, updated_at: new Date().toISOString() })
              .eq('id', byPhone.id);
            linked = true;
          }
        }
        if (!linked) {
          await supabase.from('credit_customers').insert({
            merchant_id: merchantId,
            full_name: user.name || user.user_name,
            phone: user.telephone || null,
            linked_user_id: user.id,
            status: 'active',
          });
        }
        setStatus('success');
        setMessage('Votre compte est maintenant lié. Le commerçant vous verra dans son carnet.');
        return;
      }

      setStatus('error');
      setMessage('Lien d\u2019invitation invalide.');
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Une erreur est survenue.');
    }
  }, [user, token, merchantParam]);

  useEffect(() => {
    if (authLoading) return;
    run();
  }, [authLoading, run]);

  const redirectTarget = `/credit/join${token ? `?token=${token}` : merchantParam ? `?merchant=${merchantParam}` : ''}`;

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-background-50 border border-background-200/70 rounded-2xl p-8 text-center">
        {status === 'loading' && (
          <>
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="mt-4 text-sm text-foreground-500">Vérification de l&apos;invitation...</p>
          </>
        )}

        {status === 'needLogin' && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-primary-600"></i>
            </div>
            <h1 className="text-lg font-bold font-heading text-foreground-950 mb-2">Connectez-vous</h1>
            <p className="text-sm text-foreground-500 mb-6">
              Connectez-vous à votre compte Zifek pour lier votre carnet de crédit.
            </p>
            <Link
              to={`/login-client?redirect=${encodeURIComponent(redirectTarget)}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline hover:bg-primary-600 transition-colors"
            >
              <i className="ri-login-circle-line"></i>Se connecter
            </Link>
          </>
        )}

        {(status === 'success' || status === 'already') && (
          <>
            <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-2xl text-accent-600"></i>
            </div>
            <h1 className="text-lg font-bold font-heading text-foreground-950 mb-2">
              {status === 'already' ? 'Déjà lié' : 'Compte lié !'}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{message}</p>
            <Link to="/credit" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline hover:bg-primary-600 transition-colors">
              <i className="ri-book-2-line"></i>Voir mon crédit
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500"></i>
            </div>
            <h1 className="text-lg font-bold font-heading text-foreground-950 mb-2">Impossible de lier le compte</h1>
            <p className="text-sm text-foreground-500 mb-6">{message}</p>
            <Link to="/credit" className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium no-underline hover:bg-background-200/70 transition-colors">
              Retour
            </Link>
          </>
        )}
      </div>
    </div>
  );
}